/*!
 * Copyright (c) REWE Digital GmbH
 */
import { h, Host } from "@stencil/core";
/**
 * VxMegaMenu component.
 * This component is used to render a mega menu with multiple levels.
 *
 * @example
 * <vx-mega-menu menuItems={menuItems}></vx-mega-menu>
 *
 * @part menu-item-wrapper-level-[x] - The container for each menu item.
 * @part active-menu-item-level-[x] - The active menu item.
 * @part menu-item-level-[x] - A menu item.
 * @part active-link-item-level-[x] - The active link item.
 * @part link-item-level-[x] - A link item.
 * @part title - The title of a menu item.
 */
// eslint-disable-next-line require-jsdoc
export class VxMegaMenu {
    constructor() {
        /**
         * Increased the counter of hovered menus.
         */
        this.increaseMenuHoveredCounter = (hasChildren) => hasChildren && this.hoveredMenus++;
        /**
         * Determines if a given menu item is active based on the current URL path.
         *
         * @param {MenuItem} item - The menu item to check.
         */
        this.isActiveMenuItem = (item) => {
            var _a, _b;
            return ((_a = item === null || item === void 0 ? void 0 : item.categoryIds) === null || _a === void 0 ? void 0 : _a.includes(this.categoryId)) || location.pathname === item.url ||
                ((_b = item.children) === null || _b === void 0 ? void 0 : _b.filter(item => this.isActiveMenuItem(item)).length) > 0;
        };
        this.menuItems = undefined;
        this.snippets = {
            navigationLabel: 'Hauptnavigation',
        };
        this.isActiveItemUnderlined = false;
        this.categoryId = undefined;
        this.activeMenu = null;
        this.hoveredMenus = 0;
    }
    /**
     * Resets the counter for the hovered menus
     * when the component is exited.
     */
    resetHoveredMenusCounter() {
        this.hoveredMenus = 0;
    }
    /**
     * @inheritDoc
     */
    render() {
        return (h(Host, { key: 'feb85e5028669692824b06b767bfcdd7d1f22c58' }, h("nav", { key: '640b1f04d9fc982d83cfe4245aecb80dd023fd9d', role: "menubar", "aria-label": this.snippets.navigationLabel, tabindex: "0" }, this.renderMenuItems(this.menuItems))));
    }
    /**
     * Renders the menu items recursively.
     */
    renderMenuItems(items, level = 0) {
        return items === null || items === void 0 ? void 0 : items.map((item) => {
            const { id, url, naviTrackingLink, title, children, image, placeholder } = item;
            const hasChildren = (children === null || children === void 0 ? void 0 : children.length) > 0;
            const isActive = this.isActiveMenuItem(item);
            return (h("div", { role: "none", class: "item-wrapper", part: `menu-item-wrapper-level-${level}` }, (placeholder === null || placeholder === void 0 ? void 0 : placeholder.length) && h("div", { style: { width: placeholder } }), image && !placeholder && (h("vx-image", { format: "avif", src: image, alt: "" })), h("div", { role: "group", key: id, "aria-label": title, "aria-haspopup": hasChildren, "aria-expanded": this.activeMenu === id, part: (isActive ? 'active-menu-item' : 'menu-item') + `-level-${level}`, onMouseEnter: () => this.increaseMenuHoveredCounter(hasChildren), class: {
                    [`menu-item level-${level !== null && level !== void 0 ? level : 0}`]: true,
                    'animate': this.shouldAnimate,
                    'has-children': hasChildren,
                    active: isActive,
                } }, url ? [
                h("a", { href: url !== null && url !== void 0 ? url : '#', role: "menuitem", title: title, "aria-label": title, tabindex: "0", part: (isActive ? 'active-link-item' : 'link-item') + `-level-${level}`, style: { pointerEvents: location.pathname === item.url ? 'none' : 'auto' }, onClick: e => hasChildren && this.preventClickIfTouchDevice(e), "navi-tracking-view": this.isTouchDevice ? 'mobile' : 'desktop', "navi-tracking-link": naviTrackingLink }, title),
                this.isActiveItemUnderlined && h("div", { class: "underline" }),
            ] : (h("div", { class: "title" }, title)), children && (h("div", { class: { 'sub-menu': true, 'active': this.activeMenu === id }, role: "group", "aria-label": title }, this.renderMenuItems(children, level + 1))))));
        });
    }
    /**
     * Determines if the whole navigation should animate.
     */
    get shouldAnimate() {
        return this.hoveredMenus <= 1;
    }
    /**
     * Prevents the default click event if the device is a touch device.
     *
     * @param {UIEvent} event - The event to prevent.
     */
    preventClickIfTouchDevice(event) {
        if (this.isTouchDevice) {
            event.preventDefault();
        }
    }
    /**
     * Checks if the current device is a touch device.
     *
     * @returns {boolean} - `true` if the current device is a mobile device, `false` otherwise.
     */
    get isTouchDevice() {
        return 'ontouchstart' in window;
    }
    static get is() { return "vx-mega-menu"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-mega-menu.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-mega-menu.css"]
        };
    }
    static get properties() {
        return {
            "menuItems": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "MenuItems",
                    "resolved": "MenuItem[]",
                    "references": {
                        "MenuItems": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/vx-mega-menu/types/index.ts::MenuItems"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The menu items to be displayed in the mega menu.\nChildren is an array of menu items, allowing for nested menus."
                }
            },
            "snippets": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "{ navigationLabel: string; }",
                    "resolved": "{ navigationLabel: string; }",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The snippets for the navigation."
                },
                "defaultValue": "{\n        navigationLabel: 'Hauptnavigation',\n    }"
            },
            "isActiveItemUnderlined": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean to determine if the active item should be underlined."
                },
                "attribute": "is-active-item-underlined",
                "reflect": false,
                "defaultValue": "false"
            },
            "categoryId": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The current category ID. Optional."
                },
                "attribute": "category-id",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "activeMenu": {},
            "hoveredMenus": {}
        };
    }
    static get listeners() {
        return [{
                "name": "mouseleave",
                "method": "resetHoveredMenusCounter",
                "target": undefined,
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=vx-mega-menu.js.map
